/**
 * Failure detail view for steps that fell back to agent.
 *
 * @module cache-debug/CacheDebugFailureDetail
 */
import React from 'react';
import { Text } from '@chatsdet/ui';
import { Check, X, Minus } from '@phosphor-icons/react';
import { CopyablePre } from '../../../../components/CopyButton';
import type {
  CacheDebugSnapshot,
  CacheDebugPlaybookStep,
  CacheDebugStepOutcome,
  CacheDebugPreEjectionSnapshot,
} from './useCacheDebugData';

type Props = {
  cacheOutcomeReason: string | null;
  debugSnapshots: CacheDebugSnapshot[];
  playbookSteps: CacheDebugPlaybookStep[] | null;
  stepOutcomes: CacheDebugStepOutcome[] | null;
  preEjectionSnapshot: CacheDebugPreEjectionSnapshot | null;
};

export function CacheDebugFailureDetail({
  cacheOutcomeReason,
  debugSnapshots,
  playbookSteps,
  stepOutcomes,
  preEjectionSnapshot,
}: Props) {
  return (
    <div className="space-y-3">
      {cacheOutcomeReason && (
        <Text size="xs" className="text-amber-600 dark:text-amber-400">
          Reason: {cacheOutcomeReason}
        </Text>
      )}

      {debugSnapshots.map((snap, idx) => (
        <div
          key={idx}
          className="border border-red-200 dark:border-red-800 rounded p-2 space-y-1 bg-red-50/50 dark:bg-red-900/10"
        >
          <Text size="xs" weight="medium">
            Failed at action #{snap.failedActionIndex} ({snap.failedActionType})
            {snap.elementDescription && `: "${snap.elementDescription}"`}
          </Text>
          {snap.pageUrl && (
            <Text size="xs" className="text-muted-foreground font-mono truncate">
              {snap.pageUrl}
            </Text>
          )}
          {snap.attemptedSelectors !== null && snap.attemptedSelectors !== undefined && (
            (() => {
              const selectorsText = String(
                typeof snap.attemptedSelectors === 'string'
                  ? snap.attemptedSelectors
                  : JSON.stringify(snap.attemptedSelectors, null, 2)
              );
              return (
                <div className="mt-1">
                  <Text size="xs" className="text-muted-foreground mb-0.5">
                    Attempted selectors:
                  </Text>
                  <CopyablePre value={selectorsText} label="attempted selectors">
                    <pre className="text-[10px] font-mono bg-gray-100 dark:bg-gray-800 rounded p-1.5 overflow-x-auto max-h-24">
                      {selectorsText}
                    </pre>
                  </CopyablePre>
                </div>
              );
            })()
          )}
        </div>
      ))}

      {playbookSteps && playbookSteps.length > 0 && (
        <PlaybookCompletion playbookSteps={playbookSteps} stepOutcomes={stepOutcomes} />
      )}

      {preEjectionSnapshot && <PreEjectionSection snapshot={preEjectionSnapshot} />}
    </div>
  );
}

function PlaybookCompletion({
  playbookSteps,
  stepOutcomes,
}: {
  playbookSteps: CacheDebugPlaybookStep[];
  stepOutcomes: CacheDebugStepOutcome[] | null;
}) {
  const outcomeByOrder = new Map(stepOutcomes?.map((o) => [o.stepOrder, o]) ?? []);

  return (
    <div>
      <Text size="xs" weight="medium" className="text-muted-foreground mb-1">
        Playbook completion:
      </Text>
      <div className="space-y-0.5">
        {playbookSteps.map((step) => {
          const outcome = outcomeByOrder.get(step.order);
          const icon = !outcome ? (
            <Minus size={11} className="text-gray-400" />
          ) : outcome.success ? (
            <Check size={11} weight="bold" className="text-green-500" />
          ) : (
            <X size={11} weight="bold" className="text-red-500" />
          );

          return (
            <div key={step.order} className="flex items-center gap-1.5 text-xs">
              {icon}
              <span className="text-muted-foreground tabular-nums">{step.order}.</span>
              <span className={!outcome ? 'text-muted-foreground' : ''}>
                {step.action} {step.target}
              </span>
              {outcome && !outcome.success && outcome.error && (
                <span className="text-red-500 text-[10px]"> &larr; failed</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PreEjectionSection({ snapshot }: { snapshot: CacheDebugPreEjectionSnapshot }) {
  const { cacheSummary, playbook } = snapshot;
  const gapCount = cacheSummary.compiledEntryTypes?.filter((t) => t === 'gap').length ?? 0;

  return (
    <details className="border border-gray-200 dark:border-gray-700 rounded bg-gray-50/50 dark:bg-gray-800/30">
      <summary className="cursor-pointer px-2 py-1.5 flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
        <span className="font-medium">Pre-ejection state</span>
        <span className="text-[10px] bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded">
          {cacheSummary.actionCount} actions, {playbook.length} steps
          {(cacheSummary.playbookState ?? cacheSummary.playbookHealth) &&
            `, ${cacheSummary.playbookState ?? cacheSummary.playbookHealth}`}
        </span>
      </summary>
      <div className="px-2 pb-2 space-y-2 border-t border-gray-200 dark:border-gray-700 pt-1.5">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
          <span>
            Cache: <span className="font-mono">{cacheSummary.cacheId.slice(0, 8)}</span>
          </span>
          {cacheSummary.playbookState ? (
            <span>
              State: <span className="font-medium">{cacheSummary.playbookState}</span>
            </span>
          ) : cacheSummary.playbookHealth ? (
            <span>
              Health: <span className="font-medium">{cacheSummary.playbookHealth}</span>
            </span>
          ) : null}
          {cacheSummary.compilationScore !== null &&
            cacheSummary.compilationScore !== undefined && (
              <span>
                Compilation:{' '}
                <span className="font-medium">
                  {Math.round(cacheSummary.compilationScore * 100)}%
                </span>
              </span>
            )}
          <span>
            Actions: <span className="font-medium">{cacheSummary.actionCount}</span>
          </span>
          {gapCount > 0 && (
            <span>
              Gaps:{' '}
              <span className="font-medium text-amber-600 dark:text-amber-400">{gapCount}</span>
            </span>
          )}
        </div>

        <div>
          <Text size="xs" weight="medium" className="text-muted-foreground mb-0.5">
            Original playbook:
          </Text>
          <div className="space-y-0.5">
            {playbook.map((step) => (
              <div
                key={step.order}
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <span className="tabular-nums">{step.order}.</span>
                <span>
                  {step.action} {step.target}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </details>
  );
}
